import { createClient } from '@supabase/supabase-js';
import { getCachedData, cacheData } from './performance';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Variables de entorno:', {
    url: supabaseUrl ? 'presente' : 'ausente',
    key: supabaseAnonKey ? 'presente' : 'ausente'
  });
  throw new Error('Las variables de entorno de Supabase no están configuradas correctamente');
}

// Configuración de reintentos
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

// Configuración mejorada del cliente Supabase
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    storageKey: 'supabase.auth.token'
  },
  global: {
    headers: {
      'x-client-info': 'asistente-trading-ia'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  },
  db: {
    schema: 'public'
  }
});

// Función para reintentar operaciones fallidas con backoff exponencial
export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = MAX_RETRIES,
  initialDelay: number = RETRY_DELAY
): Promise<T> => {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      console.error(`Intento ${i + 1} fallido:`, error);
      lastError = error;
      
      if (i < maxRetries - 1) {
        const delay = initialDelay * Math.pow(2, i); // Backoff exponencial
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

// Función para obtener datos con caché
export const fetchWithCache = async <T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = CACHE_TTL
): Promise<T> => {
  const cached = getCachedData(key);
  if (cached) return cached;

  const data = await retryOperation(fetcher);
  cacheData(key, data, ttl);
  return data;
};

// Función para verificar la conexión con reintentos
export const checkSupabaseConnection = async () => {
  return retryOperation(async () => {
    try {
      const { data, error } = await supabase
        .from('analyses')
        .select('count', { count: 'exact', head: true })
        .limit(1);
      
      if (error) {
        console.error('Error verificando conexión:', error);
        throw error;
      }

      return true;
    } catch (err) {
      console.error('Error en checkSupabaseConnection:', err);
      throw err;
    }
  });
};

// Función para manejar errores de Supabase
export const handleSupabaseError = (error: any): string => {
  console.error('Error detallado:', error);

  if (!navigator.onLine) {
    return 'No hay conexión a internet. Por favor, verifica tu conexión y vuelve a intentarlo.';
  }

  if (error?.message?.includes('Failed to fetch') || error?.code === 'NETWORK_ERROR') {
    reconnectSupabase(); // Intentar reconexión automática
    return 'Error de conexión. Intentando reconectar...';
  }

  if (error?.message?.includes('JWT expired')) {
    // Intentar renovar la sesión automáticamente
    supabase.auth.refreshSession();
    return 'Tu sesión ha expirado. Renovando automáticamente...';
  }

  if (error?.message?.includes('Invalid login credentials')) {
    return 'Email o contraseña incorrectos';
  }

  if (error?.status === 500) {
    return 'Error en el servidor. Por favor, inténtalo de nuevo más tarde.';
  }
  
  return error?.message || 'Ha ocurrido un error. Por favor, inténtalo de nuevo.';
};

// Función para cerrar sesión
export const signOut = async () => {
  try {
    localStorage.removeItem('supabase.auth.token');
    
    const { error } = await supabase.auth.signOut({
      scope: 'local'
    });
    
    if (error) {
      console.error('Error durante el cierre de sesión:', error);
    }

    window.location.href = '/';
  } catch (error) {
    console.error('Error durante el cierre de sesión:', error);
    window.location.href = '/';
  }
};

// Función para verificar el estado de la sesión
export const checkSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error al verificar sesión:', error);
      throw error;
    }

    if (!session) {
      throw new Error('No hay sesión activa');
    }

    return session;
  } catch (err) {
    console.error('Error en checkSession:', err);
    throw err;
  }
};

// Función para reintentar la conexión
export const reconnectSupabase = async () => {
  try {
    await checkSupabaseConnection();
    return true;
  } catch (err) {
    console.error('Error al reconectar:', err);
    return false;
  }
};

// Rate limiting for password reset attempts
const RATE_LIMIT_WINDOW = 60 * 60 * 1000; // 1 hour
const MAX_RESET_ATTEMPTS = 3;
const resetAttempts = new Map<string, { count: number; timestamp: number }>();

export const checkResetRateLimit = (email: string): boolean => {
  const now = Date.now();
  const userAttempts = resetAttempts.get(email);

  if (!userAttempts) {
    resetAttempts.set(email, { count: 1, timestamp: now });
    return true;
  }

  if (now - userAttempts.timestamp > RATE_LIMIT_WINDOW) {
    resetAttempts.set(email, { count: 1, timestamp: now });
    return true;
  }

  if (userAttempts.count >= MAX_RESET_ATTEMPTS) {
    return false;
  }

  resetAttempts.set(email, {
    count: userAttempts.count + 1,
    timestamp: userAttempts.timestamp
  });
  return true;
};

// Password validation
export const validatePassword = (password: string): { isValid: boolean; message: string } => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (password.length < minLength) {
    return {
      isValid: false,
      message: `La contraseña debe tener al menos ${minLength} caracteres`
    };
  }

  if (!hasUpperCase || !hasLowerCase) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir mayúsculas y minúsculas'
    };
  }

  if (!hasNumbers) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos un número'
    };
  }

  if (!hasSpecialChar) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos un carácter especial'
    };
  }

  return { isValid: true, message: '' };
};

// Handle password reset request
export const handlePasswordReset = async (email: string): Promise<{ success: boolean; message: string }> => {
  try {
    // Check rate limit
    if (!checkResetRateLimit(email)) {
      return {
        success: false,
        message: 'Has excedido el límite de intentos. Por favor, espera una hora.'
      };
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/update-password`
    });

    if (error) throw error;

    return {
      success: true,
      message: 'Se ha enviado un email con instrucciones para restablecer tu contraseña'
    };
  } catch (err: any) {
    console.error('Error in handlePasswordReset:', err);
    return {
      success: false,
      message: 'Error al enviar el email de recuperación. Por favor, inténtalo de nuevo.'
    };
  }
};

// Update password with validation
export const updateUserPassword = async (newPassword: string): Promise<{ success: boolean; message: string }> => {
  try {
    // Validate password strength
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      return { success: false, message: validation.message };
    }

    // Update password
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;

    // Sign out all other sessions
    await supabase.auth.signOut({ scope: 'others' });

    return {
      success: true,
      message: 'Contraseña actualizada correctamente'
    };
  } catch (err: any) {
    console.error('Error in updateUserPassword:', err);
    return {
      success: false,
      message: 'Error al actualizar la contraseña. Por favor, inténtalo de nuevo.'
    };
  }
};

// Configurar manejador de errores global para Supabase
supabase.handleError = (error: any) => {
  console.error('Error de Supabase:', error);
  if (error?.message?.includes('Failed to fetch')) {
    reconnectSupabase();
  }
};

// Configurar listener para estado de conexión
window.addEventListener('online', () => {
  console.log('🌐 Conexión restaurada, intentando reconectar...');
  reconnectSupabase();
});

// Configurar intervalo de verificación de conexión
setInterval(async () => {
  if (navigator.onLine) {
    await checkSupabaseConnection();
  }
}, 30000); // Verificar cada 30 segundos